"use client";

import { c as _c } from "react/compiler-runtime";
import { useCallback, useMemo, useState } from "react";
import { classifyFinanceStep, readStepId, requiresExternalSignature, signatureKindOf, type SignRequiredStep } from "../external-signing";
import type { ConfirmFinanceStepPayload, FinanceSignPayload, FinanceStepResponse } from "../types";
import { useExternalFinanceSigning } from "./useExternalFinanceSigning";

/**
 * The seam between "one POST and we're done" and "the user's own wallet has
 * work to do" — and, therefore, the place this client answers "is this an
 * internal or an external wallet?".
 *
 * IT IS THE BACKEND THAT ANSWERS IT, not a local flag. All seven paths have
 * the same shape: one POST, then either a terminal step (the site's CUSTODIAL
 * wallet — untouched, nothing more to do) or a `*_REQUIRED` step carrying
 * something to sign and an id. Reading the answer off that response rather
 * than off the user record means the client and the server can never disagree
 * about which kind of wallet is signing, and the POST is the same either way.
 * The wallet chooser (MetaMask / Trust Wallet) then appears on the review,
 * before any popup opens.
 *
 * THE ID IS THE POINT. `listing_id` / `offer_id` / `collection_id` from the
 * first response is kept here for the whole attempt, and every `/confirm` —
 * including after a rejected signature — goes back to that same id. A rejection
 * is retried, not restarted: no second listing, no second offer, no second
 * collection. `pending.step` moves forward as each round confirms, so a resume
 * picks up where the backend actually is.
 *
 * THE SIXTH AND SEVENTH ARE CREATE COLLECTION AND CREATE NFT, whose single round funds the chain work
 * rather than moving an NFT. It is here and not in a module of its own because
 * it is the same seam answering the same question — see the banner in
 * `../external-signing.ts` for why that file serves it too.
 */

export type StepHandoffOutcome = /** Custodial wallet, or an already-finished sequence. The caller reports success. */
{
  kind: "settled";
}
/** The wallet owes a round. The caller stays on the review and shows the panel. */ | {
  kind: "handoff";
}
/**
 * The backend asked for a round but sent nothing to act on, or no id to
 * confirm against. Reported as a failure rather than guessed at — see
 * `MissingSignPayloadError`.
 */ | {
  kind: "unusable";
};
export type ExternalStepHandoff = ReturnType<typeof useExternalStepHandoff>;
export function useExternalStepHandoff(lang, idField) {
  const $ = _c(23);
  const isFa = lang === "fa";
  const signing = useExternalFinanceSigning(lang);
  const [pending, setPending] = useState(null);
  const [awaitingIndexing, setAwaitingIndexing] = useState(false);
  let t0;
  if ($[0] !== idField) {
    t0 = (response, fallbackId) => {
      if (!requiresExternalSignature(response)) {
        return {
          kind: "settled"
        };
      }
      const classification = classifyFinanceStep(response.data?.step);
      const signPayload = response.data?.sign_payload;
      const id = readStepId(response, idField) ?? fallbackId ?? null;
      if (classification.kind !== "sign_required" || !signPayload || id === null) {
        return {
          kind: "unusable"
        };
      }
      setPending({
        step: classification.step,
        signPayload,
        id
      });
      return {
        kind: "handoff"
      };
    };
    $[0] = idField;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const receive = t0;
  let t1;
  if ($[2] !== pending || $[3] !== signing) {
    t1 = async confirmStep => {
      if (!pending) {
        return null;
      }
      setAwaitingIndexing(false);
      const result = await signing.run({
        signPayload: pending.signPayload,
        step: pending.step,
        confirmStep: payload => confirmStep(pending.id, payload),
        onStepAdvanced: t2 => {
          const {
            step,
            signPayload: signPayload_0
          } = t2;
          return setPending(current => current ? {
            ...current,
            step,
            signPayload: signPayload_0
          } : current);
        }
      });
      setAwaitingIndexing(result?.outcome === "pending_confirmation");
      return result;
    };
    $[2] = pending;
    $[3] = signing;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const sign = t1;
  let t2;
  bb0: {
    if (!pending || signing.connectedAddress) {
      t2 = null;
      break bb0;
    }
    t2 = isFa ? "\u0628\u0631\u0627\u06CC \u0627\u062F\u0627\u0645\u0647\u060C \u06A9\u06CC\u0641 \u067E\u0648\u0644 \u062E\u0648\u062F \u0631\u0627 \u0645\u062A\u0635\u0644 \u06A9\u0646\u06CC\u062F." : "Connect your wallet to continue.";
  }
  const blockedReason = t2;
  let t3;
  bb1: {
    if (!pending) {
      t3 = null;
      break bb1;
    }
    if (signatureKindOf(pending.step) === "typed_data") {
      t3 = isFa ? "\u0627\u0645\u0636\u0627 \u062F\u0631 \u06A9\u06CC\u0641 \u067E\u0648\u0644 (\u0628\u062F\u0648\u0646 \u06A9\u0627\u0631\u0645\u0632\u062F)" : "Sign in your wallet (no fee)";
      break bb1;
    }
    t3 = isFa ? "\u0627\u0631\u0633\u0627\u0644 \u062A\u0631\u0627\u06A9\u0646\u0634 \u0627\u0632 \u06A9\u06CC\u0641 \u067E\u0648\u0644" : "Send the transaction from your wallet";
  }
  const continueLabel = t3;
  const indexingNotice = awaitingIndexing ? isFa ? "\u062A\u0631\u0627\u06A9\u0646\u0634 \u0634\u0645\u0627 \u0628\u0627 \u0645\u0648\u0641\u0642\u06CC\u062A \u0631\u0648\u06CC \u0628\u0644\u0627\u06A9\u0686\u06CC\u0646 \u0627\u0646\u062C\u0627\u0645 \u0634\u062F\u060C \u0627\u0645\u0627 \u0633\u0631\u0648\u0631 \u0647\u0646\u0648\u0632 \u0622\u0646 \u0631\u0627 \u062B\u0628\u062A \u0646\u06A9\u0631\u062F\u0647 \u0627\u0633\u062A. \u062F\u0648\u0628\u0627\u0631\u0647 \u062F\u06A9\u0645\u0647\u200C\u06CC \u062A\u0627\u06CC\u06CC\u062F \u0631\u0627 \u0628\u0632\u0646\u06CC\u062F\u061B \u0647\u0645\u0627\u0646 \u062A\u0631\u0627\u06A9\u0646\u0634 \u062F\u0648\u0628\u0627\u0631\u0647 \u0628\u0631\u0631\u0633\u06CC \u0645\u06CC\u200C\u0634\u0648\u062F \u0648 \u062A\u0631\u0627\u06A9\u0646\u0634 \u062A\u0627\u0632\u0647\u200C\u0627\u06CC \u0641\u0631\u0633\u062A\u0627\u062F\u0647 \u0646\u0645\u06CC\u200C\u0634\u0648\u062F." : "Your transaction succeeded on-chain, but the server hasn't indexed it yet. Press confirm again \u2014 it re-checks the same transaction and never sends a new one." : null;
  let t4;
  if ($[5] !== signing.isBusy) {
    t4 = close => () => {
      if (signing.isBusy) {
        return;
      }
      close();
    };
    $[5] = signing.isBusy;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const guardClose = t4;
  let t5;
  if ($[7] !== signing) {
    t5 = () => {
      setPending(null);
      setAwaitingIndexing(false);
      signing.reset();
    };
    $[7] = signing;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const reset = t5;
  const t6 = pending?.step ?? null;
  let t7;
  if ($[9] !== pending) {
    t7 = pending ? signatureKindOf(pending.step) === "typed_data" : false;
    $[9] = pending;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  let t8;
  if ($[11] !== blockedReason || $[12] !== continueLabel || $[13] !== guardClose || $[14] !== indexingNotice || $[15] !== pending || $[16] !== receive || $[17] !== reset || $[18] !== sign || $[19] !== signing || $[20] !== t6 || $[21] !== t7) {
    t8 = {
      pending,
      reset,
      currentStep: t6,
      isFreeSignature: t7,
      continueLabel,
      guardClose,
      blockedReason,
      indexingNotice,
      receive,
      sign,
      signing
    };
    $[11] = blockedReason;
    $[12] = continueLabel;
    $[13] = guardClose;
    $[14] = indexingNotice;
    $[15] = pending;
    $[16] = receive;
    $[17] = reset;
    $[18] = sign;
    $[19] = signing;
    $[20] = t6;
    $[21] = t7;
    $[22] = t8;
  } else {
    t8 = $[22];
  }
  return t8;
}